/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CustomerApiService } from './customer-api.service';
import { ReportingApiService } from 'src/app/shared/services/reporting-api/reporting-api.service';
import { TokenService } from './token.service';
import {
  ReportType,
  Schedule,
  SubscriptionPayload,
  resolveReportLanguage,
  withReportLanguage,
} from 'src/app/shared/utils/reports';

export interface CreateSubscriptionDto {
  email: string;
  schedule: Schedule;
  schedulerType: Schedule;
  reportType: ReportType;
  reportFormat?: 'pdf';
  recipients: string[];
  payload: SubscriptionPayload;
}

@Injectable({
  providedIn: 'root',
})
export class ReportApiService {
  constructor(
    private api: CustomerApiService,
    private reportingApi: ReportingApiService,
    private tokenService: TokenService,
  ) {}

  // COCKPIT REPORTS (Customer API)

  storeReport(data: any): Observable<any> {
    return this.api.post('/v2/cockpit_reports/store', data);
  }

  // REPORTING SERVICE (separate base URL)

  sendReport(
    company: string,
    type: string,
    date: string,
    recipients?: string[],
  ): Observable<any> {
    const body: Record<string, unknown> = {
      date,
      company_id: company,
      report_type: type,
      language: resolveReportLanguage(),
    };
    if (recipients && recipients.length > 0) body['recipients'] = recipients;
    return this.reportingApi.post('/api/company_report', body);
  }

  sendReportPortfolio(
    portfolio: string,
    companies: string[],
    type: string,
    date: string,
    recipients?: string[],
  ): Observable<any> {
    const body: Record<string, unknown> = {
      date,
      portfolio_name: portfolio,
      company_ids: companies,
      report_type: type,
      language: resolveReportLanguage(),
    };
    if (recipients && recipients.length > 0) body['recipients'] = recipients;
    return this.reportingApi.post('/api/portfolio_report', body);
  }

  getReportStatus(
    company: string,
    type: string,
    date: string,
  ): Observable<any> {
    return this.reportingApi.get('/api/report_status', {
      company_id: company,
      report_type: type,
      date,
      language: resolveReportLanguage(),
    });
  }

  getReportStatusPortfolio(
    portfolio: string,
    companies: string[],
    type: string,
    date: string,
  ): Observable<any> {
    return this.reportingApi.post('/api/portfolio_report_status', {
      portfolio_name: portfolio,
      company_ids: companies,
      report_type: type,
      date,
      language: resolveReportLanguage(),
    });
  }

  sendQueryReport(
    searchQuery: string,
    queryName: string,
    reportType: string,
    date: string,
    additionalRecipients?: string[],
  ): Observable<any> {
    const decoded = this.tokenService.decodeToken();
    const recipients = [decoded?.email, ...(additionalRecipients ?? [])].filter(
      Boolean,
    );
    return this.reportingApi.post('/api/query_report', {
      report_type: reportType,
      date,
      search_query: searchQuery,
      query_name: queryName,
      recipients,
      format: 'pdf',
      language: resolveReportLanguage(),
    });
  }

  // SUBSCRIPTIONS (reporting service /v1/subscriptions)

  createSubscription(dto: CreateSubscriptionDto): Observable<any> {
    return this.reportingApi.post('/v1/subscriptions', withReportLanguage(dto));
  }
}
